/**
 * 折扣计算模块
 */
import { ScenarioData, DiscountType, WeeklyStrategy } from '../types';

/**
 * 计算折扣后的价格
 */
export const calculateDiscountedPrice = (
    basePrice: number,
    discountType: DiscountType,
    discountValue: number
): number => {
    if (discountType === 'amount') {
        return Math.max(0, basePrice - discountValue);
    } else {
        return basePrice * (1 - discountValue / 100);
    }
};

/**
 * 计算加权平均售价（考虑 Prime 和 Coupon）- 用于广告推演模型
 */
export const calculateEffectivePriceForScenario = (
    basePrice: number,
    data: ScenarioData
): number => {
    const primeEnabled = data.enablePrime || false;
    const couponEnabled = data.enableCoupon || false;

    // 如果都未启用，直接返回基础价格
    if (!primeEnabled && !couponEnabled) {
        return basePrice;
    }

    // 计算 Prime 价格
    const primePrice = primeEnabled && data.primeDiscountType && data.primeDiscountValue !== undefined
        ? calculateDiscountedPrice(basePrice, data.primeDiscountType, data.primeDiscountValue)
        : basePrice;

    // 订单分类（使用率转换为小数）
    const primeRateDecimal = (data.primeUsageRate || 0) / 100;
    const couponRateDecimal = (data.couponUsageRate || 0) / 100;

    const primeCouponRate = primeRateDecimal * couponRateDecimal;           // Prime + Coupon
    const primeOnlyRate = primeRateDecimal * (1 - couponRateDecimal);       // 仅 Prime
    const couponOnlyRate = (1 - primeRateDecimal) * couponRateDecimal;     // 仅 Coupon
    const normalRate = (1 - primeRateDecimal) * (1 - couponRateDecimal);   // 普通订单

    // 各类订单的实际售价
    const primeCouponPrice = couponEnabled && data.couponDiscountType && data.couponDiscountValue !== undefined
        ? calculateDiscountedPrice(primePrice, data.couponDiscountType, data.couponDiscountValue)
        : primePrice;
    const primeOnlyPrice = primePrice;
    const couponOnlyPrice = couponEnabled && data.couponDiscountType && data.couponDiscountValue !== undefined
        ? calculateDiscountedPrice(basePrice, data.couponDiscountType, data.couponDiscountValue)
        : basePrice;
    const normalPrice = basePrice;

    // 加权平均售价
    const effectivePrice =
        primeCouponPrice * primeCouponRate +
        primeOnlyPrice * primeOnlyRate +
        couponOnlyPrice * couponOnlyRate +
        normalPrice * normalRate;

    return effectivePrice;
};

/**
 * 计算 Coupon 成本
 */
export const calculateCouponCost = (
    totalSales: number,
    couponUsageRate: number
): number => {
    const couponUsagePercent = couponUsageRate / 100;
    const fixedFee = 5.0;                   // $5 固定费用
    const variableFeeRate = 0.025;          // 2.5% 佣金

    // Coupon 总成本 = (固定费 + 销售额 × 佣金率) × 使用率
    const couponCost = (fixedFee + totalSales * variableFeeRate) * couponUsagePercent;

    return couponCost;
};

/**
 * 计算叠加折扣后的实际售价 - 用于90天运营策略
 */
export const calculateEffectivePrice = (strategy: WeeklyStrategy): number => {
    let price = strategy.basePrice;

    // 步骤1: 应用 Prime 折扣
    if (strategy.enablePrime && strategy.primeDiscount > 0) {
        price = price * (1 - strategy.primeDiscount / 100);
    }

    // 步骤2: 应用 Coupon 或 Promotion（互斥）
    if (strategy.enableCoupon && strategy.couponDiscount > 0) {
        price = price * (1 - strategy.couponDiscount / 100);
    } else if (strategy.enablePromotion && strategy.promotionDiscount > 0) {
        price = price * (1 - strategy.promotionDiscount / 100);
    }

    // 步骤3: 应用 Lightning Deal（可叠加）
    if (strategy.enableLightningDeal && strategy.lightningDealDiscount > 0) {
        price = price * (1 - strategy.lightningDealDiscount / 100);
    }

    return price;
};
